import React from "react";
import {
	Switch,
	Drawer,
	DrawerOverlay,
	DrawerContent,
	DrawerHeader,
	DrawerBody,
	DrawerFooter,
	DrawerCloseButton,
} from "@chakra-ui/react";
import { useCurrentUser } from "hooks/auth";

interface EditCoverPageDrawerProps {
	coverCreatedByRole: string;
	coverPresentedTo: string;
	coverPresentedToRole: string;
	coverProjectName: string;
	coverShowLogo: boolean | null;
	coverSupplierRole: string;
	globalShowLogo: boolean;
	isOpen: boolean;
	onClose: () => void;
	onCoverCreatedByRoleChange: (value: string) => void;
	onCoverPresentedToChange: (value: string) => void;
	onCoverPresentedToRoleChange: (value: string) => void;
	onCoverProjectNameChange: (value: string) => void;
	onCoverShowLogoChange: (value: boolean | null) => void;
	onCoverSupplierRoleChange: (value: string) => void;
}

export const EditCoverPageDrawer: React.FC<EditCoverPageDrawerProps> = ({
	isOpen,
	onClose,
	coverProjectName,
	coverSupplierRole,
	coverPresentedTo,
	coverPresentedToRole,
	coverShowLogo,
	coverCreatedByRole,
	globalShowLogo,
	onCoverProjectNameChange,
	onCoverSupplierRoleChange,
	onCoverPresentedToChange,
	onCoverPresentedToRoleChange,
	onCoverShowLogoChange,
	onCoverCreatedByRoleChange,
}) => {
	const auth = useCurrentUser();
	const createdByName = auth?.currentUser?.full_name ?? "";

	const effectiveShowLogo = coverShowLogo !== null ? coverShowLogo : globalShowLogo;

	return (
		<Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
			<DrawerOverlay />
			<DrawerContent>
				<DrawerCloseButton />
				<DrawerHeader borderBottomWidth="1px" fontSize="md">
					Edit Cover Page
				</DrawerHeader>
				<DrawerBody py={5}>
					<div className="flex flex-col gap-5">
						{/* Project Name */}
						<div className="flex flex-col gap-1">
							<label
								htmlFor="cover-project-name"
								className="text-sm font-medium text-gray-700"
							>
								Project Name
							</label>
							<input
								id="cover-project-name"
								type="text"
								value={coverProjectName}
								onChange={(e) => onCoverProjectNameChange(e.target.value)}
								placeholder="Enter project name..."
								maxLength={255}
								className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
							/>
						</div>

						{/* Supplier Role */}
						<div className="flex flex-col gap-1">
							<label
								htmlFor="cover-supplier-role"
								className="text-sm font-medium text-gray-700"
							>
								Supplier Role
							</label>
							<input
								id="cover-supplier-role"
								type="text"
								value={coverSupplierRole}
								onChange={(e) => onCoverSupplierRoleChange(e.target.value)}
								placeholder="e.g. Head Contractor, Consultant..."
								maxLength={255}
								className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
							/>
						</div>

						{/* Presented To */}
						<div className="flex flex-col gap-1">
							<label
								htmlFor="cover-presented-to"
								className="text-sm font-medium text-gray-700"
							>
								Presented To
							</label>
							<input
								id="cover-presented-to"
								type="text"
								value={coverPresentedTo}
								onChange={(e) => onCoverPresentedToChange(e.target.value)}
								placeholder="Recipient name..."
								maxLength={255}
								className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
							/>
						</div>

						{/* Presented To Role */}
						<div className="flex flex-col gap-1">
							<label
								htmlFor="cover-presented-to-role"
								className="text-sm font-medium text-gray-700"
							>
								Presented To Role
							</label>
							<input
								id="cover-presented-to-role"
								type="text"
								value={coverPresentedToRole}
								onChange={(e) => onCoverPresentedToRoleChange(e.target.value)}
								placeholder="e.g. Project Director..."
								maxLength={255}
								className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
							/>
						</div>

						{/* Show Logo toggle */}
						<div className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2">
							<div>
								<p className="text-sm font-medium text-gray-700">Show Logo</p>
								<p className="text-xs text-gray-400">
									{coverShowLogo === null
										? "Using company default"
										: "Overridden for this report"}
								</p>
							</div>
							<Switch
								isChecked={effectiveShowLogo}
								onChange={(e) => onCoverShowLogoChange(e.target.checked)}
								colorScheme="blue"
								size="md"
							/>
						</div>

						{/* Created By Role */}
						<div className="flex flex-col gap-1">
							<label
								htmlFor="cover-created-by-role"
								className="text-sm font-medium text-gray-700"
							>
								Created by Role
							</label>
							<input
								id="cover-created-by-role"
								type="text"
								value={coverCreatedByRole}
								onChange={(e) => onCoverCreatedByRoleChange(e.target.value)}
								placeholder="e.g. Senior Consultant..."
								maxLength={255}
								className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
							/>
						</div>

						{/* Created By (read-only) */}
						<div className="flex flex-col gap-1">
							<label className="text-sm font-medium text-gray-700">
								Created by
							</label>
							<input
								type="text"
								value={createdByName}
								readOnly
								disabled
								className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500"
							/>
							<p className="text-xs text-gray-400">
								Automatically set from your account
							</p>
						</div>
					</div>
				</DrawerBody>
				<DrawerFooter borderTopWidth="1px">
					<button
						type="button"
						onClick={onClose}
						className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
					>
						Done
					</button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
};

